import { useState } from "react";
import { api } from "../../../api";
import { ErrorNote, Loading } from "../../../components/Feedback";
import { Badge, Btn } from "../../../components/Shared";
import ColumnStats from "./ColumnStats";

// Preview rows, per-column statistics, data quality and the validator's issues of one
// tabular file (errors first, then warnings and notes: the same validator as the DB design's
// data check): GET /api/files/{id}/profile?rows=20 (unio/services/columns.py). Loaded on
// demand, because the server reads the whole file; "Refresh" after editing the dictionary.

const cell = { padding: "6px 10px", borderTop: "1px solid #f3f4f6", whiteSpace: "nowrap", maxWidth: 240,
               overflow: "hidden", textOverflow: "ellipsis" };
const head = { padding: "8px 10px", textAlign: "left", fontSize: 12, color: "#6b7280", textTransform: "uppercase",
               letterSpacing: "0.05em", whiteSpace: "nowrap" };
const h3 = { fontSize: 15, margin: "22px 0 10px" };

export default function FileProfile({ file }) {
  const [state, setState] = useState({ data: null, error: null, loading: false });
  const load = () => {
    setState(s => ({ ...s, loading: true, error: null }));
    api.get(`/files/${file.id}/profile?rows=20`)
      .then(data => setState({ data, error: null, loading: false }))
      .catch(error => setState({ data: null, error, loading: false }));
  };
  const p = state.data;

  return (
    <section aria-label={`Preview and statistics of ${file.filename}`} style={{ marginTop: 24 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <Btn variant={p ? "secondary" : "ghost"} onClick={load} disabled={state.loading}>
          {p ? "Refresh preview and statistics" : "Show preview and statistics"}
        </Btn>
        {p && (p.issue_count > 0
          ? <Badge color="amber">{p.issue_count} value issue{p.issue_count === 1 ? "" : "s"}</Badge>
          : <Badge color="green">values fit the dictionary</Badge>)}
      </div>
      {state.loading && <Loading label="Reading the file…" />}
      <ErrorNote error={state.error} />
      {p && (
        <>
          <h3 style={h3}>Preview ({Math.min(p.preview.rows.length, p.row_count)} of {p.row_count} rows)</h3>
          <div data-table-scroll style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff" }}>
            <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
              <caption style={{ position: "absolute", left: -9999 }}>First rows of {file.filename}</caption>
              <thead><tr>{p.preview.columns.map(c => <th key={c} scope="col" style={head}>{c}</th>)}</tr></thead>
              <tbody>
                {p.preview.rows.map((r, i) => (
                  <tr key={i}>
                    {r.map((v, j) => (
                      <td key={j} style={{ ...cell, color: v === null ? "#9ca3af" : "#1a1a1a" }} title={v ?? ""}>
                        {v === null ? <span aria-label="empty">—</span> : v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {p.issues.length > 0 && (
            <>
              <h3 style={h3}>Values that do not fit the dictionary</h3>
              <div data-table-scroll style={{ overflowX: "auto", border: "1px solid rgba(217,119,6,0.3)", borderRadius: 12, background: "#fff" }}>
                <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
                  <thead><tr>
                    <th scope="col" style={head}>Column</th><th scope="col" style={head}>Row</th>
                    <th scope="col" style={head}>Value</th><th scope="col" style={head}>Problem</th>
                    <th scope="col" style={head}>Severity</th>
                  </tr></thead>
                  <tbody>
                    {p.issues.slice(0, 50).map((x, i) => (
                      <tr key={i}>
                        <td style={cell}>{x.column}</td><td style={cell}>{x.row}</td>
                        <td style={{ ...cell, fontFamily: "'JetBrains Mono', monospace" }}>{x.value}</td>
                        <td style={cell}>{x.message}</td>
                        <td style={cell}>{x.severity || "error"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {p.issue_count > 50 && <p style={{ fontSize: 12, color: "#6b7280" }}>Showing 50 of {p.issue_count}.</p>}
            </>
          )}

          {p.quality.length > 0 && (
            <>
              <h3 style={h3}>Data quality</h3>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#374151", display: "flex", flexDirection: "column", gap: 4 }}>
                {p.quality.map(q => (
                  <li key={q.column}>
                    <strong>{q.column}</strong>
                    {q.blank_count > 0 && <> — {q.blank_count} empty (rows {q.blank_rows.join(", ")}{q.blank_count > q.blank_rows.length ? ", …" : ""})</>}
                    {q.duplicate_values.length > 0 && (
                      <> — duplicates: {q.duplicate_values.slice(0, 5).map(d => `${d.value} ×${d.count}`).join(", ")}
                        {q.duplicate_values.length > 5 ? ", …" : ""}</>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          <h3 style={h3}>Statistics</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
            {Object.entries(p.stats).map(([name, stat]) => <ColumnStats key={name} name={name} stat={stat} />)}
          </div>
        </>
      )}
    </section>
  );
}
